import { Link, useNavigate } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import Logo from "../components/app/Logo";
import { useAuth } from "../context/AuthContext";
import { startBoosterCheckout, startCheckout } from "../lib/billing";

// Returns an onClick that starts Stripe checkout for `plan` when signed in, or
// routes the private operator to the login page.
function usePlanCheckout(plan) {
  const { user } = useAuth();
  const navigate = useNavigate();
  return () => {
    if (user && user !== false) startCheckout(plan);
    else navigate("/login");
  };
}

/* ============================================================================
   Plan card primitives
   ============================================================================ */

const TierPill = ({ label, color }) => (
  <span
    className="inline-flex items-center justify-center rounded-full font-mono"
    style={{
      padding: "4px 12px",
      fontSize: 11,
      letterSpacing: "0.16em",
      fontWeight: 600,
      color,
      background: `${color}1F`,
      border: `1px solid ${color}66`,
      textTransform: "uppercase",
    }}
  >
    {label}
  </span>
);

const PopularPill = () => (
  <span
    className="inline-flex items-center justify-center rounded-full font-mono"
    style={{
      padding: "5px 14px",
      fontSize: 11,
      letterSpacing: "0.14em",
      fontWeight: 600,
      color: "#0a0a0a",
      background: "var(--color-accent)",
      textTransform: "uppercase",
    }}
  >
    Most Popular
  </span>
);

const PriceBlock = ({ price }) => (
  <div className="flex items-baseline gap-2 mb-7">
    <span
      className="text-text-primary"
      style={{
        fontSize: 64,
        fontWeight: 700,
        letterSpacing: "-0.035em",
        lineHeight: 1,
      }}
    >
      {price}
    </span>
    <span
      className="text-text-tertiary"
      style={{ fontSize: 16, fontWeight: 400 }}
    >
      /month
    </span>
  </div>
);

const Feature = ({ children, kind = "yes" }) => {
  if (kind === "no") {
    return (
      <li className="flex items-start gap-3 text-text-tertiary" style={{ fontSize: 14, lineHeight: 1.5 }}>
        <Minus size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
        <span>{children}</span>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3 text-text-primary" style={{ fontSize: 14, lineHeight: 1.5 }}>
      <Check size={16} strokeWidth={2.5} color="var(--color-accent)" className="shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
};

// Renders a Link when given `to`, or a button when given `onClick` (used for
// the paid-plan CTAs that kick off Stripe checkout for signed-in visitors).
const PrimaryCardCTA = ({ to, onClick, label, testId }) => {
  const style = {
    height: 48,
    borderRadius: 10,
    background: "var(--color-accent)",
    color: "#0a0a0a",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    boxShadow: "0 14px 36px -14px rgba(45,212,191,0.55)",
  };
  const className = "motion-micro w-full inline-flex items-center justify-center";
  const enter = (e) => (e.currentTarget.style.background = "var(--color-accent-hover)");
  const leave = (e) => (e.currentTarget.style.background = "var(--color-accent)");
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testId} className={className} style={{ ...style, border: "none", cursor: "pointer" }} onMouseEnter={enter} onMouseLeave={leave}>
        {label}
      </button>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={className} style={style} onMouseEnter={enter} onMouseLeave={leave}>
      {label}
    </Link>
  );
};

const OutlineCardCTA = ({ to, onClick, label, testId }) => {
  const style = {
    height: 48,
    borderRadius: 10,
    background: "transparent",
    color: "var(--color-text-primary)",
    border: "1px solid var(--color-border-primary)",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
  };
  const className = "motion-micro w-full inline-flex items-center justify-center";
  const enter = (e) => (e.currentTarget.style.borderColor = "var(--color-text-secondary)");
  const leave = (e) => (e.currentTarget.style.borderColor = "var(--color-border-primary)");
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testId} className={className} style={{ ...style, cursor: "pointer" }} onMouseEnter={enter} onMouseLeave={leave}>
        {label}
      </button>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={className} style={style} onMouseEnter={enter} onMouseLeave={leave}>
      {label}
    </Link>
  );
};

/* ============================================================================
   Free plan — query meter callout
   ============================================================================ */

const FreePlanCard = () => {
  return (
  <div
    data-testid="plan-card-free"
    className="rounded-[20px] border flex flex-col motion-canvas"
    style={{
      background: "var(--color-background-secondary)",
      borderColor: "var(--color-border-primary)",
      padding: 32,
    }}
  >
    <div className="mb-6">
      <TierPill label="Free" color="var(--color-accent)" />
    </div>
    <PriceBlock price="$0" />
    <ul className="space-y-4 mb-10">
      <Feature>1 active project</Feature>
      <Feature>1 cloud provider per project</Feature>
      <Feature>Architecture diagram and chatbot</Feature>
      <Feature>Basic cost estimates</Feature>
      <Feature kind="no">No IaC exports (Terraform / Bicep)</Feature>
    </ul>
    <div className="mt-auto">
      <OutlineCardCTA to="/login" label="Admin sign in" testId="free-cta" />
    </div>
  </div>
  );
};

const ProPlanCard = () => {
  const onSubscribe = usePlanCheckout("pro");
  return (
  <div className="relative h-full" data-testid="plan-card-pro">
    {/* Outer glow */}
    <div
      className="absolute -inset-2 rounded-[24px] pointer-events-none"
      style={{
        background: "radial-gradient(80% 60% at 50% 50%, rgba(45,212,191,0.35), transparent 75%)",
        filter: "blur(20px)",
      }}
    />
    <div
      className="relative h-full rounded-[20px] flex flex-col motion-canvas"
      style={{
        background: "var(--color-background-secondary)",
        border: "1.5px solid var(--color-accent)",
        padding: 32,
        boxShadow: "0 30px 80px -30px rgba(45,212,191,0.4), inset 0 0 0 1px rgba(45,212,191,0.18)",
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <PopularPill />
        <span
          className="text-text-secondary"
          style={{ fontSize: 14, fontWeight: 500 }}
        >
          Pro
        </span>
      </div>
      <PriceBlock price="$29" />
      <ul className="space-y-4 mb-10">
        <Feature>5 active projects</Feature>
        <Feature>All cloud providers (AWS, Azure, GCP)</Feature>
        <Feature>Terraform IaC export</Feature>
        <Feature>Bicep export (Azure deployments)</Feature>
          <Feature>Project advisor Q&amp;A + confirmed architecture refinement</Feature>
        <Feature>Real cost estimates per service</Feature>
        <Feature>Full architecture diagrams</Feature>
        <Feature>Eligible early-access members: $19/month for the first 12 months</Feature>
      </ul>
      <div className="mt-auto">
        <PrimaryCardCTA onClick={onSubscribe} label="Upgrade to Pro" testId="pro-cta" />
      </div>
    </div>
  </div>
  );
};

/* ============================================================================
   Page
   ============================================================================ */

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div
      className="min-h-screen bg-bg-primary text-text-primary"
      data-testid="pricing-page"
    >
      {/* Top nav */}
      <nav
        className="w-full border-b border-border-secondary sticky top-0 z-40"
        style={{ background: "rgba(10,10,10,0.85)", backdropFilter: "blur(12px)" }}
      >
        <div className="max-w-[1200px] mx-auto px-6 h-[64px] flex items-center justify-between">
          <Link to="/" style={{ textDecoration: "none" }}>
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="nav-signin-link"
              className="px-3 h-9 inline-flex items-center text-text-secondary hover:text-text-primary motion-micro"
              style={{ fontSize: 13, textDecoration: "none" }}
            >
              Sign In
            </Link>
            <Link
              to="/login"
              data-testid="nav-getstarted-btn"
              className="px-5 h-10 inline-flex items-center justify-center rounded-[10px] motion-micro"
              style={{
                fontSize: 14,
                fontWeight: 600,
                background: "var(--color-accent)",
                color: "#0a0a0a",
                textDecoration: "none",
                boxShadow: "0 1px 0 rgba(255,255,255,0.18) inset",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
            >
              Admin sign in
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-6 pt-24 pb-12 text-center">
          <h1
            className="text-text-primary fade-up"
            style={{
              fontSize: "clamp(40px, 5.5vw, 64px)",
              fontWeight: 700,
              letterSpacing: "-0.035em",
              lineHeight: 1.05,
            }}
            data-testid="pricing-headline"
          >
            Full Pricing Tier Reference
          </h1>
          <p
            className="text-text-secondary mt-5 mx-auto fade-up"
            style={{
              fontSize: 16,
              lineHeight: 1.6,
              maxWidth: 540,
              animationDelay: "120ms",
            }}
          >
            Everything you need to know about each plan
          </p>
        </div>
      </section>

      {/* Plan grid */}
      <section className="relative pb-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div
            className="grid gap-7 fade-up grid-cols-1 sm:grid-cols-2 items-stretch max-w-[800px] mx-auto"
            style={{ animationDelay: "200ms" }}
            data-testid="plan-grid"
          >
            <FreePlanCard />
            <ProPlanCard />
          </div>

          <div className="mt-8 rounded-[16px] border p-6 flex flex-wrap items-center justify-between gap-4"
            data-testid="booster-offer" style={{ background: "var(--color-background-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div>
              <p className="text-text-primary font-semibold">Need more architecture runs?</p>
              <p className="text-text-secondary text-sm mt-1">Add 20 runs for $10. Your projects and exports stay available when the monthly fair-use limit is reached.</p>
            </div>
            <button type="button" data-testid="booster-cta" onClick={() => user ? startBoosterCheckout() : navigate("/login")}
              className="rounded-lg px-4 py-2 text-sm font-semibold"
              style={{ background: "var(--color-accent)", color: "#0a0a0a" }}>Buy 20 runs · $10</button>
          </div>

          <p
            className="text-center text-text-tertiary mt-12"
            style={{ fontSize: 13, lineHeight: 1.6 }}
          >
            Free plan available without a card. Pro is billed monthly.
          </p>
        </div>
      </section>

      {/* Footer (minimal — matches the rest of the marketing surface) */}
      <footer
        className="border-t"
        style={{ borderColor: "var(--color-border-primary)" }}
      >
        <div className="max-w-[1200px] mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
          <span
            className="font-mono text-text-tertiary"
            style={{ fontSize: 11, letterSpacing: "0.04em" }}
          >
            © 2026 Guided Sentry. All rights reserved.
          </span>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              className="text-text-tertiary motion-micro"
              style={{ fontSize: 12, textDecoration: "none" }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-text-tertiary motion-micro"
              style={{ fontSize: 12, textDecoration: "none" }}
            >
              Terms of Service
            </Link>
            <Link
              to="/company/security"
              className="text-text-tertiary motion-micro"
              style={{ fontSize: 12, textDecoration: "none" }}
            >
              Security
            </Link>
            <Link
              to="/company/contact"
              className="text-text-tertiary motion-micro"
              style={{ fontSize: 12, textDecoration: "none" }}
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
